import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import axios from 'axios';
import { 
  Copy, Check, RotateCcw, Zap, 
  Activity, Terminal, Music2, Globe2,
  Mic, Gauge, Image, Download, Trash2, Clock,
  Star, Play, Square, Heart, LogOut, Crown, Lock, MessageCircle, Users
} from 'lucide-react';
import LandingPage from './LandingPage';
import AuthCallback from './AuthCallback';
import AdminPanel from './AdminPanel';

// Error Boundary to catch DOM manipulation errors from browser extensions
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    if (error.message?.includes('insertBefore') || error.message?.includes('removeChild') || error.message?.includes('appendChild')) {
      // DOM manipulation error from browser extension - auto-recover
      this.setState({ hasError: true });
      setTimeout(() => this.setState({ hasError: false }), 100);
    }
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || this.props.children;
    }
    return this.props.children;
  }
}

const API = `${window.location.origin}/api`;

// Create axios instance with Bearer token interceptor
const api = axios.create({ withCredentials: true });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('muno_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const GENRES = [
  // BRASIL - Expandido
  { category: 'Brasil', name: 'Sertanejo', emoji: '🤠', subgenres: ['Universitário', 'Raiz / Modão', 'Arrochanejo', 'Sertanejo Pop', 'Arrocha'], tags: 'brazilian sertanejo, acoustic steel string guitar, clean accordion, emotional male/female vocals' },
  { category: 'Brasil', name: 'Samba', emoji: '🥁', subgenres: ['Samba de Roda', 'Partido Alto', 'Samba-Enredo', 'Samba Rock', 'Samba Canção'], tags: 'brazilian samba, cavaquinho, surdo percussion, pandeiro, organic group vocals, rhythmic' },
  { category: 'Brasil', name: 'Pagode', emoji: '🎤', subgenres: ['Pagode Romântico', 'Pagode 90s', 'Pagode Raiz', 'Pagode Universitário'], tags: 'brazilian pagode, cavaquinho, tantan, banjo, group vocals, party vibe, romantic' },
  { category: 'Brasil', name: 'Funk & Urban', emoji: '⚡', subgenres: ['Funk RJ (150bpm)', 'Funk SP (Mandela)', 'Trap Brasil', 'Funk Consciente', 'Brazilian Phonk'], tags: 'brazilian funk beat, heavy sub bass, repetitive percussion, urban vibe' },
  { category: 'Brasil', name: 'MPB & Bossa', emoji: '🎸', subgenres: ['MPB Clássica', 'Bossa Nova', 'Tropicalia', 'Jazz Brasil'], tags: 'nylon string guitar, sophisticated harmony, soft vocals, bossa nova groove' },
  { category: 'Brasil', name: 'Gospel', emoji: '🙏', subgenres: ['Pentecostal', 'Evangélico', 'Religioso', 'Contemporâneo'], tags: 'brazilian gospel, worship, spiritual, piano, acoustic guitar, choir, inspiring vocals' },
  { category: 'Brasil', name: 'Regional Norte', emoji: '🌳', subgenres: ['Tecnobrega', 'Carimbó', 'Guitarrada', 'Brega Pop', 'Música da Saudade', 'Brega Lento', 'Siriá', 'Toada de Boi Bumbá'], tags: 'brazilian brega, electronic beats, synthesizer melodies, northern brazilian rhythm' },
  { category: 'Brasil', name: 'Regional Nordeste', emoji: '☀️', subgenres: ['Piseiro', 'Forró das Antigas', 'Xote', 'Baião', 'Axé', 'Frevo', 'Maracatu', 'Arrocha', 'Arrocha Bahiano', 'Forró Eletrônico', 'Forró Pé de Serra', 'Forró Estilizado', 'Embolada', 'Coco', 'Cacuriá', 'Manguebeat'], tags: 'brazilian northeastern rhythm, percussion ensemble, festive energy, regional instruments' },
  { category: 'Brasil', name: 'Regional Centro-Oeste', emoji: '🌾', subgenres: ['Moda de Viola', 'Guarânia', 'Cururu', 'Catira', 'Rasqueado', 'Polca Paraguaia', 'Siriri'], tags: 'viola de cocho, traditional gaucho, paraguay influence, rural brazilian, acoustic' },
  { category: 'Brasil', name: 'Regional Sudeste', emoji: '🏙️', subgenres: ['Funk Carioca', 'Samba Paulista', 'Música Mineira', 'Sertanejo Paulista', 'Rap Nacional'], tags: 'urban brazilian, metropolitan sound, diverse production, modern brazilian music' },
  { category: 'Brasil', name: 'Regional Sul', emoji: '🏇', subgenres: ['Gaúcha / Tradicionalista', 'Vanerão', 'Chamamé', 'Milonga', 'Kachaka'], tags: 'southern brazilian music, accordion, guitar, traditional gaucho style' },

  // AMÉRICA LATINA
  { category: 'América Latina', name: 'Latina', emoji: '💃', subgenres: ['Reggaeton', 'Salsa', 'Bachata', 'Cumbia', 'Merengue', 'Dembow'], tags: 'latin rhythm, dembow beat, spanish vocals, caribbean influence' },
  { category: 'América Latina', name: 'Regional Mexicano', emoji: '🌮', subgenres: ['Corridos Tumbados', 'Mariachi', 'Banda', 'Norteño', 'Ranchera'], tags: 'mexican music, accordion, brass section, bajo sexto, regional mexican' },
  { category: 'América Latina', name: 'Urbano Latino', emoji: '🔥', subgenres: ['Trap Latino', 'Reggaeton Moderno', 'Latin Drill', 'Perreo'], tags: 'latin urban, modern production, heavy bass, trap beats, spanish flow' },
  
  // CARIBE
  { category: 'Caribe', name: 'Reggae & Dancehall', emoji: '🏝️', subgenres: ['Roots Reggae', 'Dancehall', 'Dub', 'Reggae Fusion'], tags: 'reggae offbeat, bass heavy, caribbean vibe, relaxed tempo' },
  { category: 'Caribe', name: 'Afro-Caribe', emoji: '🥁', subgenres: ['Soca', 'Calypso', 'Zouk', 'Kompa'], tags: 'caribbean percussion, island rhythm, steel drums, festive tropical' },
  
  // ÁFRICA
  { category: 'África', name: 'Afrobeats', emoji: '🌍', subgenres: ['Afrobeats', 'Afro-Fusion', 'Afro-Pop', 'Alte'], tags: 'african drums, afrobeats rhythm, nigerian sound, west african' },
  { category: 'África', name: 'Amapiano & SA', emoji: '🇿🇦', subgenres: ['Amapiano', 'Gqom', 'Kwaito', 'South African House'], tags: 'log drum, piano chords, south african house, deep bass, shuffled percussion' },
  
  // ÁSIA
  { category: 'Ásia', name: 'K-Pop & J-Pop', emoji: '🎌', subgenres: ['K-Pop', 'J-Pop', 'K-R&B', 'City Pop'], tags: 'asian pop, catchy hooks, polished production, electronic elements' },
  { category: 'Ásia', name: 'Música Asiática', emoji: '🏯', subgenres: ['Bollywood', 'C-Pop', 'Thai Pop', 'Indo Pop'], tags: 'asian music, traditional fusion, modern asian pop, cultural blend' },
  
  // EUROPA
  { category: 'Europa', name: 'Eletrônica Europeia', emoji: '🎧', subgenres: ['UK Garage', 'Techno Berlin', 'French House', 'Euro Dance'], tags: 'european electronic, club music, rave culture, synthesizers' },
  { category: 'Europa', name: 'Pop Europeu', emoji: '🎵', subgenres: ['Europop', 'Schlager', 'Eurovision Style', 'Nordic Pop'], tags: 'european pop, melodic, radio friendly, modern production' },
  
  // AMÉRICA DO NORTE
  { category: 'América do Norte', name: 'Hip Hop & R&B', emoji: '🎧', subgenres: ['Trap', 'Drill', 'Boom Bap', 'Contemporary R&B', 'Neo Soul', 'Jersey Club'], tags: 'hip hop beat, 808 bass, rap vocals, urban production' },
  { category: 'América do Norte', name: 'Pop & Rock', emoji: '🎤', subgenres: ['Synthpop', 'Indie Rock', 'Hard Rock', 'Alt Pop', 'Hyperpop'], tags: 'modern radio pop, polished production, catchy hooks, driving energy' },
  { category: 'América do Norte', name: 'Country & Folk', emoji: '🤠', subgenres: ['Modern Country', 'Country Pop', 'Americana', 'Folk'], tags: 'acoustic guitar, country twang, storytelling, american roots' },
  
  // RITMOS MODERNOS (Tendências 2024-2025)
  { category: 'Ritmos Modernos', name: 'Eletrônica Moderna', emoji: '⚡', subgenres: ['Hyperpop', 'Pluggnb', 'Brazilian Phonk', 'Drift Phonk', 'Jersey Club'], tags: 'modern electronic, viral sound, tiktok trending, experimental production' },
  { category: 'Ritmos Modernos', name: 'Global Fusion', emoji: '🌐', subgenres: ['Amapiano Fusion', 'Afro House', 'Latin EDM', 'K-Pop Fusion'], tags: 'genre blending, global crossover, hybrid sound, modern fusion' },
  { category: 'Ritmos Modernos', name: 'Underground 2025', emoji: '🔊', subgenres: ['Drill UK', 'Plugg', 'Rage', 'Gabber', 'Speed Garage'], tags: 'underground trending, micro-community, social media viral, hard hitting' }
];

const VOCALS = [
  { name: 'Feminino', tags: 'female vocals, feminine voice' },
  { name: 'Masculino', tags: 'male vocals, masculine voice' },
  { name: 'Dueto', tags: 'duet, male and female vocals' },
  { name: 'Coral', tags: 'choir, chorus, group vocals' },
  { name: 'Sussurrado', tags: 'whispered vocals, intimate' },
  { name: 'Assoviado', tags: 'whistling, melodic whistle' },
  { name: 'Rap/Falado', tags: 'rap vocals, spoken word' },
  { name: 'Instrumental', tags: 'no vocals, instrumental' }
];

const TEMPOS = [
  { name: 'Lenta', range: '60-80 BPM', tags: 'slow tempo, 60-80 bpm' },
  { name: 'Normal', range: '80-100 BPM', tags: 'moderate tempo, 80-100 bpm' },
  { name: 'Rápida', range: '110-130 BPM', tags: 'fast tempo, 110-130 bpm' },
  { name: 'Super Rápida', range: '130-150 BPM', tags: 'very fast tempo, 130-150 bpm' }
];

const MOODS = [
  { name: 'Energético', tags: 'high energy, intense' },
  { name: 'Romântico', tags: 'romantic, sentimental' },
  { name: 'Alegre', tags: 'happy, positive vibe' },
  { name: 'Sombrio', tags: 'dark, moody' },
  { name: 'Melancólico', tags: 'sad, emotional' },
  { name: 'Festivo', tags: 'party, celebration' },
  { name: 'Relaxante', tags: 'chill, laid back' },
  { name: 'Nostálgico', tags: 'nostalgic, memories, longing' },
  { name: 'Inspirador', tags: 'inspiring, motivational, uplifting' }
];

const COVER_STYLES = [
  { name: 'Padrão', prompt: 'artistic, modern, abstract art with vibrant colors and textures' },
  { name: 'Minimalista', prompt: 'minimalist, clean lines, simple geometric shapes, solid colors, negative space, modern design' },
  { name: 'Caricatura', prompt: 'caricature style, exaggerated features, cartoon illustration, colorful, humorous, hand-drawn feel' },
  { name: 'Desenho/Ilustração', prompt: 'hand-drawn illustration, sketchy lines, artistic drawing, watercolor textures, creative illustration' },
  { name: 'Pixel Art', prompt: 'pixel art style, retro 8-bit aesthetic, blocky pixels, retro gaming vibes, nostalgic digital art' },
  { name: 'Neon/Cyberpunk', prompt: 'neon lights, cyberpunk aesthetic, dark background with glowing neon colors, futuristic, synthwave' },
  { name: 'Psicodélico', prompt: 'psychedelic art, trippy patterns, vivid swirling colors, kaleidoscopic, 1960s-70s inspired' },
  { name: 'Fotorrealista', prompt: 'photorealistic, ultra-detailed, cinematic lighting, dramatic composition, high-end photography style' },
  { name: 'Abstrato', prompt: 'abstract expressionism, bold brushstrokes, splatter paint, emotional color palette, fine art' },
  { name: 'Vintage/Retrô', prompt: 'vintage aesthetic, retro 70s-80s style, faded colors, grain texture, old-school album cover feel' },
  { name: 'Anime/Mangá', prompt: 'anime art style, manga-inspired illustration, Japanese animation aesthetic, vibrant cel-shaded' },
  { name: 'Collage', prompt: 'collage art, mixed media, cut-out paper textures, layered elements, magazine clipping aesthetic' },
  { name: '3D Render', prompt: '3D rendered art, glossy surfaces, volumetric lighting, cinema 4D style, modern 3D illustration' },
  { name: 'Graffiti/Street', prompt: 'street art, graffiti style, spray paint textures, urban wall art, bold tags and murals' }
];

function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('limit');
  const [rhythm, setRhythm] = useState(GENRES[0].name);
  const [subgenre, setSubgenre] = useState(GENRES[0].subgenres[0]);
  const [moods, setMoods] = useState([MOODS[0].name]); // Changed to array for multiple selection
  const [theme, setTheme] = useState('');
  const [lyricsLang, setLyricsLang] = useState('pt');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  
  // Mode: 'normal', 'similar', 'remix'
  const [mode, setMode] = useState('normal');
  const [artistName, setArtistName] = useState('');
  const [songName, setSongName] = useState('');
  
  // Remix mode: second rhythm
  const [rhythm2, setRhythm2] = useState(GENRES[3].name); // Default: Funk & Urban
  const [subgenre2, setSubgenre2] = useState(GENRES[3].subgenres[0]);
  
  // New states for vocal and tempo
  const [vocal, setVocal] = useState(VOCALS[1].name); // Default: Masculino
  const [tempo, setTempo] = useState(TEMPOS[1].name); // Default: Normal

  // Cover generation states
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverStyle, setCoverStyle] = useState(COVER_STYLES[0].name);

  // Cover history (localStorage, max 5)
  const [coverHistory, setCoverHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('muno_cover_history');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const saveCoverToHistory = useCallback((base64) => {
    setCoverHistory(prev => {
      const entry = { id: Date.now(), image: base64, date: new Date().toLocaleString('pt-BR') };
      const updated = [entry, ...prev].slice(0, 5);
      localStorage.setItem('muno_cover_history', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removeCoverFromHistory = useCallback((id) => {
    setCoverHistory(prev => {
      const updated = prev.filter(c => c.id !== id);
      localStorage.setItem('muno_cover_history', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearCoverHistory = useCallback(() => {
    localStorage.removeItem('muno_cover_history');
    setCoverHistory([]);
  }, []);

  const downloadFromHistory = (base64, id) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64}`;
    link.download = `muno-cover-${id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Templates/Favoritos (localStorage, max 10)
  const [templates, setTemplates] = useState(() => {
    try {
      const saved = localStorage.getItem('muno_templates');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const saveTemplate = useCallback(() => {
    if (!templateName.trim()) return;
    setTemplates(prev => {
      const entry = {
        id: Date.now(),
        name: templateName.trim(),
        rhythm, subgenre, vocal, tempo,
        moods: [...moods]
      };
      const updated = [entry, ...prev].slice(0, 10);
      localStorage.setItem('muno_templates', JSON.stringify(updated));
      return updated;
    });
    setTemplateName('');
    setShowSaveTemplate(false);
  }, [templateName, rhythm, subgenre, vocal, tempo, moods]);

  const loadTemplate = useCallback((t) => {
    setRhythm(t.rhythm);
    const g = GENRES.find(item => item.name === t.rhythm);
    if (g && g.subgenres.includes(t.subgenre)) {
      setSubgenre(t.subgenre);
    } else if (g) {
      setSubgenre(g.subgenres[0]);
    }
    setVocal(t.vocal);
    setTempo(t.tempo);
    if (t.moods && t.moods.length > 0) setMoods(t.moods);
    setMode('normal');
  }, []);

  const removeTemplate = useCallback((id) => {
    setTemplates(prev => {
      const updated = prev.filter(t => t.id !== id);
      localStorage.setItem('muno_templates', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // BPM Metronome
  const [bpmPlaying, setBpmPlaying] = useState(false);
  const bpmIntervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [bpmPulse, setBpmPulse] = useState(false);

  const getCurrentBpm = useCallback(() => {
    const t = TEMPOS.find(t => t.name === tempo);
    if (!t) return 90;
    const match = t.range.match(/(\d+)-(\d+)/);
    if (match) return Math.round((parseInt(match[1]) + parseInt(match[2])) / 2);
    return 90;
  }, [tempo]);

  const playClick = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1000;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.08);
    } catch {}
  }, []);

  const toggleMetronome = useCallback(() => {
    if (bpmPlaying) {
      clearInterval(bpmIntervalRef.current);
      bpmIntervalRef.current = null;
      setBpmPlaying(false);
      setBpmPulse(false);
    } else {
      const bpm = getCurrentBpm();
      const interval = 60000 / bpm;
      setBpmPlaying(true);
      playClick();
      setBpmPulse(true);
      setTimeout(() => setBpmPulse(false), 100);
      bpmIntervalRef.current = setInterval(() => {
        playClick();
        setBpmPulse(true);
        setTimeout(() => setBpmPulse(false), 100);
      }, interval);
    }
  }, [bpmPlaying, getCurrentBpm, playClick]);

  // Stop metronome when tempo changes
  useEffect(() => {
    if (bpmPlaying) {
      clearInterval(bpmIntervalRef.current);
      const bpm = getCurrentBpm();
      const interval = 60000 / bpm;
      bpmIntervalRef.current = setInterval(() => {
        playClick();
        setBpmPulse(true);
        setTimeout(() => setBpmPulse(false), 100);
      }, interval);
    }
    return () => clearInterval(bpmIntervalRef.current);
  }, [tempo, bpmPlaying, getCurrentBpm, playClick]);

  const [processingAuth, setProcessingAuth] = useState(false);

  // Auth: handle session_id hash callback
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash?.includes('session_id=')) return;
    
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    
    setProcessingAuth(true);
    const sessionId = match[1];
    
    // Clear hash immediately
    window.history.replaceState(null, '', '/app');
    setCurrentPath('/app');
    
    api.post(`${API}/auth/session`, { session_id: sessionId })
      .then(async (res) => {
        // Save session token to localStorage for reliable auth
        if (res.data?.session_token) {
          localStorage.setItem('muno_session_token', res.data.session_token);
        }
        // Re-fetch from /auth/me to get the latest plan
        try {
          const meRes = await api.get(`${API}/auth/me`);
          console.log('[AUTH] /auth/me response:', meRes.data);
          setUser(meRes.data);
        } catch {
          setUser(res.data);
        }
        setAuthChecked(true);
        setProcessingAuth(false);
      })
      .catch(err => {
        console.error("Auth error:", err);
        setAuthChecked(true);
        setProcessingAuth(false);
      });
  }, []);

  // Auth: check for existing session (only if not processing callback)
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    api.get(`${API}/auth/me`)
      .then(async (res) => {
        console.log('[AUTH] Existing session /auth/me:', res.data);
        setUser(res.data);
        setAuthChecked(true);
        // Auto-check for pending upgrades on every session restore
        try {
          const upRes = await api.post(`${API}/check-upgrade`);
          if (upRes.data?.upgraded && res.data?.plan !== 'pro') {
            setUser(prev => ({ ...prev, plan: 'pro' }));
          }
        } catch {}
      })
      .catch(() => {
        // No valid session - clear stale token
        localStorage.removeItem('muno_session_token');
        setAuthChecked(true);
      });
  }, []);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await api.post(`${API}/auth/logout`, {});
    } catch {}
    localStorage.removeItem('muno_session_token');
    setUser(null);
    window.location.href = '/';
  };

  const handleAuthComplete = async (userData) => {
    setUser(userData);
    setAuthChecked(true);
    try {
      const res = await api.post(`${API}/check-upgrade`, {});
      if (res.data?.upgraded) {
        setUser({ ...userData, plan: 'pro' });
      }
    } catch {}
  };

  const handleUpgrade = () => {
    const stripeUrl = 'https://buy.stripe.com/9B67sMcPj2xdfK1fKu0Jq03';
    const email = user?.email ? `?prefilled_email=${encodeURIComponent(user.email)}` : '';
    window.open(stripeUrl + email, '_blank');
  };

  const refreshUser = async () => {
    try {
      const res = await api.get(`${API}/auth/me`);
      console.log('[AUTH] refreshUser /auth/me:', res.data);
      setUser(res.data);
    } catch {}
  };

  // Handle payment success polling
  useEffect(() => {
    if (currentPath !== '/payment-success') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) { window.location.href = '/'; return; }
    
    const poll = async () => {
      try {
        const res = await api.get(`${API}/checkout/status/${sessionId}`);
        if (res.data?.payment_status === 'paid') {
          await refreshUser();
          window.history.replaceState(null, '', '/app');
          setCurrentPath('/app');
        }
      } catch {
        window.history.replaceState(null, '', '/app');
        setCurrentPath('/app');
      }
    };
    const timer = setTimeout(poll, 2000);
    return () => clearTimeout(timer);
  }, [currentPath]);

  const currentGenreData = useMemo(() => GENRES.find(g => g.name === rhythm) || GENRES[0], [rhythm]);
  const currentGenreData2 = useMemo(() => GENRES.find(g => g.name === rhythm2) || GENRES[3], [rhythm2]);

  const groupedGenres = useMemo(() => {
    return GENRES.reduce((acc, genre) => {
      if (!acc[genre.category]) acc[genre.category] = [];
      acc[genre.category].push(genre);
      return acc;
    }, {});
  }, []);

  const handleMoodToggle = (moodName) => {
    setMoods(prev => {
      if (prev.includes(moodName)) {
        // Don't allow removing if it's the only one selected (minimum 1 required)
        if (prev.length === 1) {
          return prev; // Keep the current selection
        }
        // Remove if already selected and there are others
        return prev.filter(m => m !== moodName);
      } else {
        // Add if not selected, but limit to 2
        if (prev.length >= 2) {
          // Replace the first one with the new one
          return [prev[1], moodName];
        }
        return [...prev, moodName];
      }
    });
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setResult(null);
    try {
      const requestData = {
        theme: theme.trim() || "",
        use_similar_style: mode === 'similar',
        use_remix: mode === 'remix',
        lyrics_language: lyricsLang
      };

      if (mode === 'similar') {
        // Similar style mode - vibes são neutralizadas
        if (!artistName.trim() || !songName.trim()) {
          alert('Por favor, preencha o nome do cantor e da música!');
          setIsLoading(false);
          return;
        }
        requestData.artist_name = artistName.trim();
        requestData.song_name = songName.trim();
        requestData.genre = "";
        requestData.subgenre = "";
        requestData.mood = "neutral";
        requestData.vocal = vocal;
        requestData.speed = TEMPOS.find(t => t.name === tempo)?.tags || "";
      } else if (mode === 'remix') {
        // Remix mode - fusão de 2 estilos
        if (moods.length === 0) {
          alert('Por favor, selecione pelo menos uma vibe!');
          setIsLoading(false);
          return;
        }
        requestData.mood = moods.join(' + ');
        requestData.genre = rhythm;
        requestData.subgenre = subgenre;
        requestData.genre2 = rhythm2;
        requestData.subgenre2 = subgenre2;
        requestData.vocal = vocal;
        requestData.speed = TEMPOS.find(t => t.name === tempo)?.tags || "";
      } else {
        // Normal mode - vibes são obrigatórias
        if (moods.length === 0) {
          alert('Por favor, selecione pelo menos uma vibe!');
          setIsLoading(false);
          return;
        }
        const moodString = moods.join(' + ');
        requestData.mood = moodString;
        requestData.genre = rhythm;
        requestData.subgenre = subgenre;
        requestData.artist_name = "";
        requestData.song_name = "";
        requestData.vocal = vocal;
        requestData.speed = TEMPOS.find(t => t.name === tempo)?.tags || "";
      }

      console.log('API URL:', API);
      console.log('Request data:', requestData);

      const response = await api.post(`${API}/generate-prompt`, requestData);

      if (response.data) {
        setResult(response.data);
        refreshUser(); // Update usage count
      }
    } catch (err) {
      console.error('Error details:', err);
      if (err.response?.status === 403) {
        setUpgradeReason('limit');
        setShowUpgradeModal(true);
      } else if (err.response?.status === 401) {
        handleLogin();
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Erro desconhecido ao gerar prompt';
        alert(`Erro ao gerar: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copy = (text, type) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(type);
          setTimeout(() => setCopied(null), 2000);
        }).catch((err) => {
          console.error('Erro ao copiar:', err);
          // Fallback para método antigo
          copyFallback(text, type);
        });
      } else {
        // Fallback para navegadores que não suportam clipboard API
        copyFallback(text, type);
      }
    } catch (err) {
      console.error('Erro na função copy:', err);
      alert('Não foi possível copiar. Por favor, copie manualmente.');
    }
  };

  const copyFallback = (text, type) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Erro no fallback:', err);
      alert('Erro ao copiar. Tente selecionar o texto manualmente.');
    }
  };

  const handleGenerateCover = async () => {
    setCoverLoading(true);
    setCoverImage(null);
    try {
      const selectedStyle = COVER_STYLES.find(s => s.name === coverStyle) || COVER_STYLES[0];
      const coverData = {
        genre: mode === 'similar' ? '' : mode === 'remix' ? `${rhythm} + ${rhythm2}` : rhythm,
        subgenre: mode === 'similar' ? '' : mode === 'remix' ? `${subgenre} + ${subgenre2}` : subgenre,
        mood: mode === 'similar' ? 'neutral' : moods.join(' + '),
        theme: theme.trim() || '',
        style_tags: result?.style || '',
        visual_style: selectedStyle.prompt
      };
      const response = await api.post(`${API}/generate-cover`, coverData);
      if (response.data?.image_base64) {
        setCoverImage(response.data.image_base64);
        saveCoverToHistory(response.data.image_base64);
      }
    } catch (err) {
      console.error('Error generating cover:', err);
      if (err.response?.status === 403) {
        setUpgradeReason('limit');
        setShowUpgradeModal(true);
      } else if (err.response?.status === 401) {
        handleLogin();
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Erro ao gerar capa';
        alert(`Erro: ${errorMessage}`);
      }
    } finally {
      setCoverLoading(false);
    }
  };

  const downloadCover = () => {
    if (!coverImage) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${coverImage}`;
    link.download = `muno-cover-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearAll = useCallback(() => {
    setResult(null);
    setCoverImage(null);
    setCoverStyle(COVER_STYLES[0].name);
    setTheme('');
    setMoods([MOODS[0].name]);
    setRhythm(GENRES[0].name);
    setSubgenre(GENRES[0].subgenres[0]);
    setRhythm2(GENRES[3].name);
    setSubgenre2(GENRES[3].subgenres[0]);
    setVocal(VOCALS[1].name);
    setTempo(TEMPOS[1].name);
    setMode('normal');
    setArtistName('');
    setSongName('');
    setLyricsLang('pt');
    setCopied(null);
    setShowSaveTemplate(false);
    setTemplateName('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Auto-refresh Pro status when tab regains focus (after Stripe payment)
  useEffect(() => {
    const handleFocus = () => {
      if (user) refreshUser();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  // ==================== RENDER ====================
  // Compute app state for rendering
  const isPro = user?.plan === 'pro';
  const promptsUsed = user?.prompt_count_today || 0;
  const dailyLimit = user?.daily_limit || 2;
  const promptsRemaining = isPro ? 'Ilimitado' : `${Math.max(0, dailyLimit - promptsUsed)}/${dailyLimit}`;
  
  const showPayment = currentPath === '/payment-success';
  const showAdmin = currentPath === '/admin';
  const showLanding = !showPayment && !showAdmin && currentPath !== '/app';
  const showLoading = !showPayment && !showAdmin && !showLanding && (!authChecked || processingAuth);
  const showLogin = !showPayment && !showAdmin && !showLanding && !showLoading && !user;
  const showApp = !showPayment && !showAdmin && !showLanding && !showLoading && !showLogin;

  return (
    <div id="app-root" translate="no">
      {/* Payment Processing */}
      {showPayment && (
        <div className="min-h-screen flex items-center justify-center app-gradient">
          <div className="text-center">
            <Crown size={48} className="text-yellow-400 mx-auto mb-4" />
            <p className="text-xl font-bold text-white mb-2">Processando pagamento...</p>
            <p className="text-cyan-300 text-sm">Aguarde, você será redirecionado.</p>
          </div>
        </div>
      )}

      {/* Landing Page */}
      {showLanding && <LandingPage onStart={handleLogin} />}

      {showAdmin && <AdminPanel />}

      {/* Loading */}
      {showLoading && (
        <div className="min-h-screen flex items-center justify-center app-gradient">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-cyan-300 text-sm font-bold">{processingAuth ? 'Autenticando...' : 'Carregando...'}</p>
          </div>
        </div>
      )}

      {/* Login Screen */}
      {showLogin && (
        <div className="min-h-screen flex items-center justify-center app-gradient">
          <div className="text-center">
            <h1 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-3xl font-black text-cyan-400 mb-4">MUNOMUSIC</h1>
            <p className="text-white/70 text-sm mb-8">Faça login para acessar o app</p>
            <button 
              onClick={handleLogin}
              className="px-8 py-3 rounded-full font-bold text-sm text-black transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #00F0FF, #39FF14)' }}
              data-testid="app-login-button"
            >
              Entrar com Google
            </button>
            <p className="mt-6">
              <button onClick={() => { window.location.href = '/'; }} className="text-cyan-300/60 text-xs hover:text-cyan-300 underline">
                Voltar para Landing Page
              </button>
            </p>
          </div>
        </div>
      )}

      {/* === MAIN APP === */}
      {showApp && (
        <div className="min-h-screen pb-20 px-4 flex flex-col items-center app-gradient">

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="upgrade-modal">
          <div className="card-purple rounded-3xl p-8 max-w-md mx-4 border border-purple-500/30 text-center">
            <Crown size={48} className="text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              {upgradeReason === 'group' ? 'Grupo Exclusivo Pro' : isPro ? 'Erro' : 'Limite Atingido!'}
            </h3>
            <p className="text-purple-200 text-sm mb-6">
              {upgradeReason === 'group'
                ? 'O Grupo VIP no WhatsApp é exclusivo para assinantes MUNOMUSIC Pro. Assine e tenha acesso ao grupo, suporte direto e dicas exclusivas!'
                : isPro 
                  ? 'Ocorreu um erro. Tente novamente.'
                  : 'Você usou seus 2 prompts gratuitos de hoje. Assine o Pro para prompts e capas ilimitados!'
              }
            </p>
            {!isPro && (
              <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30">
                <p className="text-2xl font-black text-white">R$ 29,90<span className="text-sm font-normal text-purple-300">/mês</span></p>
                <p className="text-xs text-yellow-300 mt-1">Prompts ilimitados + Capas com IA</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowUpgradeModal(false)} className="flex-1 py-3 rounded-xl text-sm font-bold border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 transition-all">
                Fechar
              </button>
              {!isPro && (
                <button onClick={() => { setShowUpgradeModal(false); handleUpgrade(); }} className="flex-1 py-3 rounded-xl text-sm font-black bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-400 hover:to-orange-400 transition-all" data-testid="upgrade-button">
                  Assinar Pro
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl w-full py-12">
        {/* User Bar */}
        <div className="w-full max-w-6xl flex items-center justify-between mb-4 px-2" data-testid="user-bar">
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-cyan-500/50" />}
            <span className="text-xs text-cyan-200 font-bold">{user?.name || user?.email}</span>
            {isPro ? (
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 text-yellow-300 text-[10px] font-black uppercase">
                <Crown size={10} /> Pro
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[10px] font-black uppercase">
                Grátis
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[10px] text-purple-300 font-bold">
              Prompts hoje: <span className={isPro ? 'text-yellow-300' : promptsUsed >= dailyLimit ? 'text-red-400' : 'text-green-400'}>{promptsRemaining}</span>
            </span>
            {!isPro && (
              <button onClick={handleUpgrade} className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-400 hover:to-orange-400 transition-all" data-testid="upgrade-top-button">
                <Crown size={10} /> Pro R$29,90
              </button>
            )}
            {isPro ? (
              <a href="https://chat.whatsapp.com/DiVRr1XZryn7W3vdgHrFpE" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-500 hover:to-green-400 transition-all" data-testid="whatsapp-group-button">
                <Users size={10} /> Grupo VIP
              </a>
            ) : (
              <button onClick={() => { setUpgradeReason('group'); setShowUpgradeModal(true); }} className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-500 hover:to-green-400 transition-all" data-testid="whatsapp-group-locked">
                <Users size={10} /> Grupo VIP
              </button>
            )}
            <button onClick={handleLogout} className="text-purple-400 hover:text-red-400 transition-all" title="Sair" data-testid="logout-button">
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Pro Banner - only for free users */}
        {!isPro && (
          <div className="w-full max-w-6xl mb-6 px-2" data-testid="pro-banner">
            <div 
              className="w-full p-4 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 border border-yellow-500/30 cursor-pointer hover:border-yellow-400/50 transition-all"
              style={{ background: 'linear-gradient(135deg, rgba(234,179,8,0.1), rgba(249,115,22,0.1))' }}
              onClick={handleUpgrade}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500">
                  <Crown size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-black text-white">Desbloqueie o MUNOMUSIC Pro</p>
                  <p className="text-[10px] text-yellow-200/70">Prompts ilimitados + Capas com IA + Todos os ritmos • <span className="font-bold text-yellow-300">R$ 29,90/mês</span></p>
                </div>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); handleUpgrade(); }}
                className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-400 hover:to-orange-400 transition-all shadow-lg whitespace-nowrap"
                style={{ boxShadow: '0 0 20px rgba(234,179,8,0.3)' }}
                data-testid="pro-banner-button"
              >
                Assinar Pro
              </button>
            </div>
          </div>
        )}

        {/* WhatsApp Group Banner - only for Pro users */}
        {isPro && (
          <div className="w-full max-w-6xl mb-6 px-2" data-testid="pro-whatsapp-banner">
            <a 
              href="https://chat.whatsapp.com/DiVRr1XZryn7W3vdgHrFpE" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full p-4 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 border border-green-500/30 hover:border-green-400/50 transition-all block"
              style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(22,163,74,0.15))' }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-green-600">
                  <MessageCircle size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-black text-white">Grupo Exclusivo MUNOMUSIC Pro</p>
                  <p className="text-[10px] text-green-200/70">Tire dúvidas, compartilhe criações e receba dicas exclusivas</p>
                </div>
              </div>
              <span className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg whitespace-nowrap">
                Entrar no Grupo
              </span>
            </a>
          </div>
        )}

        <header className="flex flex-col items-center text-center gap-6 mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-900 to-green-900 text-white text-[10px] font-black tracking-[0.2em] uppercase shadow-lg glow-badge">
            <Globe2 size={14} className="animate-pulse" /> Maestro Universal
          </div>
          
          <div className="logo-container">
            <img 
              src="/images/muno-logo.jpg" 
              alt="MUNOMUSIC Logo" 
              className="logo-image"
              data-testid="logo"
            />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <section className="space-y-6">
            <div className="card-purple rounded-[40px] p-10 shadow-2xl border border-purple-500/30">
              <div className="flex items-center gap-4 mb-10">
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-green-500 rounded-2xl text-white shadow-lg glow-icon">
                  <Music2 size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tight text-cyan-200">Criação</h2>
                  <p className="text-[10px] font-bold text-green-300 uppercase tracking-widest">Configure seu som</p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Toggle between modes */}
                <div className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-2xl border border-cyan-500/20 flex-wrap">
                  <button
                    onClick={() => setMode('normal')}
                    className={`px-5 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                      mode === 'normal'
                        ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg glow-button' 
                        : 'bg-transparent text-cyan-300/50 hover:text-cyan-300'
                    }`}
                    data-testid="mode-normal"
                  >
                    <Music2 size={14} className="inline mr-1" /> Normal
                  </button>
                  <div className="w-px h-8 bg-cyan-500/30 hidden sm:block"></div>
                  <button
                    onClick={() => setMode('remix')}
                    className={`px-5 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                      mode === 'remix'
                        ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg glow-button' 
                        : 'bg-transparent text-orange-300/50 hover:text-orange-300'
                    }`}
                    data-testid="mode-remix"
                  >
                    <Zap size={14} className="inline mr-1" /> Remix
                  </button>
                  <div className="w-px h-8 bg-cyan-500/30 hidden sm:block"></div>
                  <button
                    onClick={() => setMode('similar')}
                    className={`px-5 py-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                      mode === 'similar'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg glow-button' 
                        : 'bg-transparent text-pink-300/50 hover:text-pink-300'
                    }`}
                    data-testid="mode-similar"
                  >
                    <Mic size={14} className="inline mr-1" /> Similar
                  </button>
                </div>

                {/* Conditional fields based on mode */}
                {mode === 'similar' ? (
                  /* Similar Style Mode Fields */
                  <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="p-4 bg-pink-900/20 rounded-2xl border border-pink-500/30">
                      <p className="text-[10px] text-pink-300 font-bold mb-3 text-center">
                        A IA vai analisar o estilo da música e gerar tags profissionais SEM mencionar o cantor
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-pink-300 uppercase tracking-widest">Nome do Cantor</label>
                      <input
                        type="text"
                        value={artistName}
                        onChange={e => setArtistName(e.target.value)}
                        placeholder="Ex: Gustavo Lima"
                        className="w-full input-dark p-4 rounded-2xl font-bold text-pink-300 outline-none focus:ring-4 ring-pink-500/50 border-2 border-pink-500/30"
                        data-testid="artist-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-pink-300 uppercase tracking-widest">Nome da Música</label>
                      <input
                        type="text"
                        value={songName}
                        onChange={e => setSongName(e.target.value)}
                        placeholder="Ex: Balada"
                        className="w-full input-dark p-4 rounded-2xl font-bold text-pink-300 outline-none focus:ring-4 ring-pink-500/50 border-2 border-pink-500/30"
                        data-testid="song-input"
                      />
                    </div>
                  </div>
                ) : mode === 'remix' ? (
                  /* Remix Mode Fields - 2 rhythms side by side */
                  <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="p-4 bg-gradient-to-r from-orange-900/20 to-yellow-900/20 rounded-2xl border border-orange-500/30">
                      <p className="text-[10px] text-orange-300 font-bold text-center">
                        Escolha 2 ritmos diferentes e a IA vai criar uma fusão única dos estilos!
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Ritmo 1 */}
                      <div className="space-y-3 p-4 rounded-2xl border-2 border-cyan-500/30 bg-cyan-900/10">
                        <p className="text-[10px] font-black text-cyan-300 uppercase tracking-widest text-center">Ritmo 1</p>
                        <div className="space-y-2">
                          <select 
                            value={rhythm} 
                            onChange={e => {
                              setRhythm(e.target.value);
                              const g = GENRES.find(item => item.name === e.target.value);
                              if (g) setSubgenre(g.subgenres[0]);
                            }}
                            className="w-full input-dark p-3 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer text-sm"
                            data-testid="remix-rhythm1-select"
                          >
                            {Object.entries(groupedGenres).map(([category, items]) => (
                              <optgroup key={category} label={category.toUpperCase()}>
                                {items.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                              </optgroup>
                            ))}
                          </select>
                          <select 
                            value={subgenre} 
                            onChange={e => setSubgenre(e.target.value)}
                            className="w-full input-dark p-3 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer text-sm"
                            data-testid="remix-subgenre1-select"
                          >
                            {currentGenreData.subgenres.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      {/* Ritmo 2 */}
                      <div className="space-y-3 p-4 rounded-2xl border-2 border-orange-500/30 bg-orange-900/10">
                        <p className="text-[10px] font-black text-orange-300 uppercase tracking-widest text-center">Ritmo 2</p>
                        <div className="space-y-2">
                          <select 
                            value={rhythm2} 
                            onChange={e => {
                              setRhythm2(e.target.value);
                              const g = GENRES.find(item => item.name === e.target.value);
                              if (g) setSubgenre2(g.subgenres[0]);
                            }}
                            className="w-full input-dark p-3 rounded-2xl font-bold text-orange-300 outline-none focus:ring-4 ring-orange-500/50 cursor-pointer text-sm"
                            data-testid="remix-rhythm2-select"
                          >
                            {Object.entries(groupedGenres).map(([category, items]) => (
                              <optgroup key={category} label={category.toUpperCase()}>
                                {items.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                              </optgroup>
                            ))}
                          </select>
                          <select 
                            value={subgenre2} 
                            onChange={e => setSubgenre2(e.target.value)}
                            className="w-full input-dark p-3 rounded-2xl font-bold text-orange-300 outline-none focus:ring-4 ring-orange-500/50 cursor-pointer text-sm"
                            data-testid="remix-subgenre2-select"
                          >
                            {currentGenreData2.subgenres.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Fusion preview */}
                    <div className="text-center p-3 bg-gradient-to-r from-cyan-900/20 via-purple-900/20 to-orange-900/20 rounded-xl border border-purple-500/20">
                      <p className="text-[10px] text-purple-300 font-bold">
                        <span className="text-cyan-300">{rhythm} ({subgenre})</span>
                        <span className="mx-2 text-yellow-400">+</span>
                        <span className="text-orange-300">{rhythm2} ({subgenre2})</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Normal Mode Fields */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-cyan-200 uppercase tracking-widest">Ritmo</label>
                      <select 
                        value={rhythm} 
                        onChange={e => {
                          setRhythm(e.target.value);
                          const g = GENRES.find(item => item.name === e.target.value);
                          if (g) setSubgenre(g.subgenres[0]);
                        }}
                        className="w-full input-dark p-4 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer"
                        data-testid="rhythm-select"
                      >
                        {Object.entries(groupedGenres).map(([category, items]) => (
                          <optgroup key={category} label={category.toUpperCase()}>
                            {items.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-cyan-300 uppercase tracking-widest">Sub-estilo</label>
                      <select 
                        value={subgenre} 
                        onChange={e => setSubgenre(e.target.value)}
                        className="w-full input-dark p-4 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer"
                        data-testid="subgenre-select"
                      >
                        {currentGenreData.subgenres.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {/* Vocal and Velocidade dropdowns - visible in both modes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-cyan-200 uppercase tracking-widest flex items-center gap-2">
                      <Mic size={12} /> Vocal
                    </label>
                    <select
                      value={vocal}
                      onChange={e => setVocal(e.target.value)}
                      className="w-full input-dark p-4 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer"
                      data-testid="vocal-select"
                    >
                      {VOCALS.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-cyan-300 uppercase tracking-widest flex items-center gap-2">
                      <Gauge size={12} /> Velocidade
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={tempo}
                        onChange={e => setTempo(e.target.value)}
                        className="flex-1 input-dark p-4 rounded-2xl font-bold text-cyan-300 outline-none focus:ring-4 ring-cyan-500/50 cursor-pointer"
                        data-testid="speed-select"
                      >
                        {TEMPOS.map(t => <option key={t.name} value={t.name}>{t.name} ({t.range})</option>)}
                      </select>
                      <button
                        onClick={toggleMetronome}
                        className={`px-4 rounded-2xl font-bold transition-all flex items-center gap-1 ${
                          bpmPlaying 
                            ? 'bg-red-500/30 border-2 border-red-400 text-red-300' 
                            : 'bg-cyan-500/20 border-2 border-cyan-500/40 text-cyan-300 hover:border-cyan-400'
                        }`}
                        title={bpmPlaying ? 'Parar' : `Ouvir ${getCurrentBpm()} BPM`}
                        data-testid="bpm-toggle"
                      >
                        {bpmPlaying ? <Square size={14} /> : <Play size={14} />}
                        <span className={`w-2.5 h-2.5 rounded-full transition-all ${
                          bpmPulse ? 'bg-green-400 scale-150 shadow-[0_0_10px_rgba(74,222,128,0.8)]' : bpmPlaying ? 'bg-green-500/50' : 'bg-cyan-500/30'
                        }`} />
                      </button>
                    </div>
                    {bpmPlaying && (
                      <p className="text-[8px] text-center text-green-300 font-bold animate-pulse">{getCurrentBpm()} BPM</p>
                    )}
                  </div>
                </div>

                {/* Templates / Favoritos - only in Normal/Remix mode */}
                {mode !== 'similar' && (
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <button
                      onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/10 transition-all"
                      data-testid="save-template-toggle"
                    >
                      <Star size={12} /> Salvar Preset
                    </button>
                    {templates.map(t => (
                      <button
                        key={t.id}
                        onClick={() => loadTemplate(t)}
                        className="group flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-bold text-cyan-200 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 hover:border-cyan-400/40 transition-all"
                        title={`${t.rhythm} · ${t.subgenre} · ${t.vocal} · ${t.tempo}`}
                        data-testid={`template-${t.id}`}
                      >
                        <Heart size={10} className="text-yellow-400" /> {t.name}
                        <span
                          onClick={(e) => { e.stopPropagation(); removeTemplate(t.id); }}
                          className="ml-1 text-red-400/50 hover:text-red-400 cursor-pointer"
                          data-testid={`remove-template-${t.id}`}
                        >
                          <Trash2 size={10} />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {showSaveTemplate && mode !== 'similar' && (
                  <div className="flex gap-2 animate-in fade-in duration-300">
                    <input
                      type="text"
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && saveTemplate()}
                      placeholder="Nome do preset (ex: Meu Sertanejo)"
                      maxLength={20}
                      className="flex-1 input-dark p-3 rounded-2xl font-bold text-yellow-300 outline-none focus:ring-4 ring-yellow-500/50 border-2 border-yellow-500/30 text-sm"
                      data-testid="template-name-input"
                    />
                    <button
                      onClick={saveTemplate}
                      disabled={!templateName.trim()}
                      className="px-5 py-3 rounded-2xl font-black uppercase text-[10px] tracking-wider bg-gradient-to-r from-yellow-500 to-orange-500 text-white disabled:opacity-30 transition-all hover:from-yellow-400 hover:to-orange-400"
                      data-testid="save-template-confirm"
                    >
                      Salvar
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    <label className={`text-[10px] font-black uppercase tracking-widest text-center ${mode === 'similar' ? 'text-gray-500' : 'text-cyan-300'}`}>
                      Vibe
                    </label>
                    {mode !== 'similar' && (
                      <span className="text-[8px] px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded-full font-bold">
                        Escolha 1 ou 2
                      </span>
                    )}
                  </div>
                  
                  {mode === 'similar' ? (
                    <div className="text-center p-4 bg-gray-900/30 rounded-xl border border-gray-700/30">
                      <p className="text-[10px] text-gray-400 italic">
                        🎵 No modo Estilo Similar, as vibes são neutralizadas para não interferir na análise pura do estilo da música escolhida
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap justify-center gap-2">
                        {MOODS.map(m => (
                          <button 
                            key={m.name} 
                            onClick={() => handleMoodToggle(m.name)}
                            className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                              moods.includes(m.name)
                                ? 'bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg glow-button scale-105' 
                                : 'bg-transparent text-cyan-300/50 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50 hover:scale-105'
                            }`}
                            data-testid={`mood-${m.name.toLowerCase()}`}
                          >
                            <span style={{ display: 'inline' }}>{moods.includes(m.name) ? `✓ ${m.name}` : m.name}</span>
                          </button>
                        ))}
                      </div>
                      <div className="text-center p-2 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-xl border border-cyan-500/20" style={{ display: moods.length > 0 ? 'block' : 'none' }}>
                        <p className="text-[10px] text-cyan-300 font-bold">
                          <span>{moods.length === 1 ? '✓ 1 Vibe: ' : '✓✓ 2 Vibes: '}</span>
                          <span className="text-white">{moods.join(' + ')}</span>
                        </p>
                        <p className="text-[8px] text-cyan-400 mt-1" style={{ display: moods.length === 2 ? 'block' : 'none' }}>
                          Máximo atingido - Clique em outra para substituir
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-cyan-300 uppercase tracking-widest">Coloque a ideia da sua composição aqui!</label>
                  <textarea 
                    value={theme} 
                    onChange={e => setTheme(e.target.value)}
                    placeholder="Deixe em branco se você quer apenas as style tags e vai escrever sua própria letra..."
                    className="w-full h-32 input-dark p-5 rounded-3xl font-medium outline-none focus:ring-4 ring-cyan-500/50 resize-none text-cyan-300 shadow-inner"
                    data-testid="theme-input"
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <label className="text-[10px] font-black text-cyan-300/60 uppercase tracking-widest">Idioma da letra:</label>
                    <div className="flex gap-2">
                      {[
                        { code: 'pt', label: 'PT', full: 'Português' },
                        { code: 'en', label: 'EN', full: 'English' },
                        { code: 'es', label: 'ES', full: 'Español' }
                      ].map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => setLyricsLang(lang.code)}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                            lyricsLang === lang.code
                              ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                              : 'bg-transparent text-cyan-300/40 border border-cyan-500/20 hover:text-cyan-300'
                          }`}
                          title={lang.full}
                          data-testid={`lang-${lang.code}`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <button 
                  onClick={handleGenerate} 
                  disabled={isLoading}
                  className="w-full py-6 btn-gradient rounded-[24px] font-black uppercase tracking-[0.2em] shadow-xl transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                  data-testid="generate-button"
                >
                  {isLoading ? <RotateCcw className="animate-spin" size={20} /> : <><Zap size={20} /> {mode === 'remix' ? 'Gerar Remix Fusion' : 'Gerar Prompt Maestro'}</>}
                </button>

                {/* Cover Style Selector */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-purple-300 uppercase tracking-widest flex items-center gap-2">
                    <Image size={12} /> Estilo Visual da Capa {!isPro && <Lock size={10} className="text-gray-500" />}
                  </label>
                  <div className="flex flex-wrap justify-center gap-2">
                    {COVER_STYLES.map(s => (
                      <button
                        key={s.name}
                        onClick={() => isPro && setCoverStyle(s.name)}
                        className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all ${
                          coverStyle === s.name
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                            : isPro
                              ? 'bg-transparent text-purple-300/60 hover:text-purple-300 border border-purple-500/30 hover:border-purple-400/50 hover:scale-105'
                              : 'bg-transparent text-gray-500/40 border border-gray-600/20 cursor-not-allowed'
                        }`}
                        data-testid={`cover-style-${s.name.toLowerCase().replace(/[\s\/]/g, '-')}`}
                        disabled={!isPro}
                      >
                        <span>{coverStyle === s.name ? `✓ ${s.name}` : s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={handleGenerateCover} 
                  disabled={coverLoading || (!isPro)}
                  className={`w-full py-5 rounded-[24px] font-black uppercase tracking-[0.2em] shadow-xl transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 ${isPro ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:from-purple-500 hover:to-pink-400' : 'bg-gray-700/50 text-gray-400 border-2 border-gray-600/30 cursor-not-allowed'}`}
                  style={isPro ? { boxShadow: '0 0 30px rgba(168, 85, 247, 0.4)' } : {}}
                  data-testid="generate-cover-button"
                  title={!isPro ? 'Disponível apenas no plano Pro' : ''}
                >
                  {coverLoading ? <RotateCcw className="animate-spin" size={20} /> : <>{!isPro && <Lock size={16} />}<Image size={20} /> Criar Capa {!isPro && <span className="text-[8px] ml-1">(PRO)</span>}</>}
                </button>

                {/* Clear All Button */}
                {result && (
                  <button 
                    onClick={clearAll}
                    className="w-full py-4 rounded-[24px] font-black uppercase tracking-[0.2em] transition-all active:scale-[0.98] flex items-center justify-center gap-3 bg-transparent text-red-400/80 border-2 border-red-500/30 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/50"
                    data-testid="clear-all-button"
                  >
                    <Trash2 size={18} /> Limpar Tudo
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-6">
            {result ? (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-6 duration-700">
                <div className="card-dark rounded-[32px] overflow-hidden shadow-2xl border border-cyan-500/30">
                  <div className="px-6 py-4 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-b border-cyan-500/20 flex justify-between items-center">
                    <span className="text-[10px] font-black text-cyan-300 uppercase tracking-widest">Style Tags</span>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => copy(result.style, 'style')} 
                        className="text-cyan-300 hover:text-cyan-100 transition-all"
                        data-testid="copy-style-button"
                        title="Copiar"
                      >
                        {copied === 'style' ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                      </button>
                      <button 
                        onClick={() => {
                          const text = encodeURIComponent(`${result.style}\n\n_Criado com MUNOMUSIC_`);
                          window.open(`https://wa.me/?text=${text}`, '_blank');
                        }}
                        className="text-green-400 hover:text-green-300 transition-all"
                        data-testid="share-whatsapp-button"
                        title="Enviar no WhatsApp"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-8 text-cyan-200 font-mono text-xl font-bold italic leading-relaxed" data-testid="style-output">{result.style}</div>
                </div>

                {result.lyrics && result.lyrics.trim() !== "" && (
                  <div className="card-purple rounded-[32px] overflow-hidden shadow-lg border border-pink-500/30">
                    <div className="px-6 py-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-b border-pink-500/20 flex justify-between items-center">
                      <span className="text-[10px] font-black text-pink-300 uppercase tracking-widest">Lyrics</span>
                      <button 
                        onClick={() => copy(result.lyrics, 'lyrics')} 
                        className="text-pink-300 hover:text-pink-100 transition-all"
                        data-testid="copy-lyrics-button"
                      >
                        {copied === 'lyrics' ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                      </button>
                    </div>
                    <div className="p-8 text-pink-200 whitespace-pre-wrap leading-relaxed font-serif italic text-lg max-h-[400px] overflow-y-auto custom-scrollbar" data-testid="lyrics-output">
                      {result.lyrics}
                    </div>
                  </div>
                )}

                {(!result.lyrics || result.lyrics.trim() === "") && (
                  <div className="card-purple rounded-[32px] overflow-hidden shadow-lg border border-pink-500/30 p-8">
                    <div className="flex flex-col items-center justify-center text-center gap-3">
                      <Music2 size={48} className="text-pink-300 opacity-50" />
                      <p className="text-pink-300 text-sm font-bold">Nenhuma letra gerada</p>
                      <p className="text-purple-300 text-xs italic">Você pode escrever sua própria letra no Suno! 🎵</p>
                    </div>
                  </div>
                )}

                {/* Cover Image Display */}
                {coverImage && (
                  <div className="card-purple rounded-[32px] overflow-hidden shadow-lg border border-purple-500/30" data-testid="cover-result">
                    <div className="px-6 py-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-b border-purple-500/20 flex justify-between items-center">
                      <span className="text-[10px] font-black text-purple-300 uppercase tracking-widest">Capa Gerada</span>
                      <button 
                        onClick={downloadCover} 
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider hover:from-green-400 hover:to-emerald-500 transition-all"
                        data-testid="download-cover-button"
                      >
                        <Download size={14} /> Baixar
                      </button>
                    </div>
                    <div className="p-6 flex justify-center">
                      <img 
                        src={`data:image/png;base64,${coverImage}`} 
                        alt="Capa gerada" 
                        className="rounded-2xl shadow-2xl max-w-full"
                        style={{ maxHeight: '400px' }}
                        data-testid="cover-image"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {coverImage && (
                  <div className="card-purple rounded-[32px] overflow-hidden shadow-lg border border-purple-500/30 animate-in fade-in duration-700" data-testid="cover-result-standalone">
                    <div className="px-6 py-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-b border-purple-500/20 flex justify-between items-center">
                      <span className="text-[10px] font-black text-purple-300 uppercase tracking-widest">Capa Gerada</span>
                      <button 
                        onClick={downloadCover} 
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider hover:from-green-400 hover:to-emerald-500 transition-all"
                        data-testid="download-cover-standalone-button"
                      >
                        <Download size={14} /> Baixar
                      </button>
                    </div>
                    <div className="p-6 flex justify-center">
                      <img 
                        src={`data:image/png;base64,${coverImage}`} 
                        alt="Capa gerada" 
                        className="rounded-2xl shadow-2xl max-w-full"
                        style={{ maxHeight: '400px' }}
                        data-testid="cover-image-standalone"
                      />
                    </div>
                  </div>
                )}
                {!coverImage && (
                  <div className="h-full min-h-[500px] border-2 border-dashed border-purple-500/30 rounded-[40px] flex flex-col items-center justify-center text-purple-300 gap-6 card-purple/50 backdrop-blur-sm">
                    <Activity size={64} className="opacity-20 animate-pulse" />
                    <p className="text-[10px] font-black uppercase tracking-[0.3em]">Configure e clique em Gerar</p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Cover History Section */}
        {coverHistory.length > 0 && (
          <div className="mt-12 w-full" data-testid="cover-history-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-purple-300" />
                <h3 className="text-sm font-black text-purple-200 uppercase tracking-widest">Últimas Capas ({coverHistory.length}/5)</h3>
              </div>
              <button
                onClick={clearCoverHistory}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider text-red-300 border border-red-500/30 hover:bg-red-500/10 transition-all"
                data-testid="clear-history-button"
              >
                <Trash2 size={12} /> Limpar Histórico
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {coverHistory.map((cover) => (
                <div key={cover.id} className="card-purple rounded-2xl overflow-hidden border border-purple-500/20 group" data-testid={`history-cover-${cover.id}`}>
                  <img
                    src={`data:image/png;base64,${cover.image}`}
                    alt="Capa"
                    className="w-full aspect-square object-cover"
                  />
                  <div className="p-3 flex items-center justify-between">
                    <span className="text-[8px] text-purple-300 font-bold">{cover.date}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadFromHistory(cover.image, cover.id)}
                        className="text-green-400 hover:text-green-300 transition-all"
                        title="Baixar"
                        data-testid={`download-history-${cover.id}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => removeCoverFromHistory(cover.id)}
                        className="text-red-400 hover:text-red-300 transition-all"
                        title="Remover"
                        data-testid={`remove-history-${cover.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 px-6 py-3 bg-gradient-to-r from-blue-900 to-green-900 rounded-full shadow-2xl border border-cyan-500/30 glow-footer">
        <span className="text-[8px] font-black text-cyan-200 uppercase tracking-widest">MUNO v1.0</span>
        <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
        <span className="text-[8px] font-black text-green-300 uppercase tracking-widest">Suno AI Optimized</span>
        <div className="w-px h-3 bg-cyan-500/30"></div>
        <a href="mailto:turbinasocial@gmail.com" className="text-[8px] font-black text-green-400 uppercase tracking-widest hover:text-green-300 transition-all flex items-center gap-1" data-testid="footer-contact">
          Suporte
        </a>
      </footer>
    </div>
      )}
    </div>
  );
}

export default function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}